// Accounting › Bills to pay's sections beside its bills (Blueprint's; PayablesPage.tsx draws the page), on
// ui-web's parts: a bill or a vendor's credit note entered straight to accounts, the receipts for parts
// bought on jobs (BL-012), the approved vendor credits to use on a bill, the returns awaiting a vendor's
// credit (BL-013) and the vendors. Each is a Section of RowList rows; a row's form opens under it as one
// of its lines, as the panels' inline forms did. Riverstone keeps PayablesEntry.tsx, Receipts.tsx and
// ReturnsAwaiting.tsx; what they decide is shared (receipts-model.ts, inventory/returns-model.ts) and
// every refusal is the server's. payables-page.css lays out the forms, tokens only.
import {
  Button,
  Choice,
  ChoiceGroup,
  ConfirmDialog,
  Link,
  Notice,
  RowList,
  Segmented,
  SelectField,
  Skeleton,
  SkeletonGroup,
  TextField,
  type Row,
  type RowTone,
} from '@flowology/ui-web/fl';
import { Section } from '@flowology/ui-web/patterns';
import { useState, type ReactNode } from 'react';
import { client } from '../api.ts';
import {
  CLOSE_LABEL,
  closeOutcome,
  creditForm,
  creditLines,
  creditSubtotal,
  daysOutText,
  mayNet,
  type AwaitingRow,
  type CloseChoice,
  type CreditLineForm,
  type VendorReturn,
} from '../inventory/returns-model.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { closedMonthHint, dayText, money, parseMoney, todayIn } from './model.ts';
import {
  approveBlocker,
  approveDefaults,
  approveInput,
  cardsOf,
  overheadAccounts,
  receiptText,
  statusText,
  supplierText,
  totalOf,
  whereText,
  type ApproveForm,
  type JobPurchase,
} from './receipts-model.ts';
import type { Vendor } from './PayablesEntry.tsx';
import type { Account, BankAccount, Period, VendorBill } from './types.ts';
import { errorText, useAct, useQuery, type Act } from './ui.tsx';

type Toast = (m: string) => void;

const accountLabel = (a: Pick<Account, 'code' | 'name'>) => `${a.code} ${a.name}`;
/** A bill goes to what was bought: an expense, or an asset that isn't money (stock, a tool, a van). */
const billable = (a: Account) =>
  a.type === 'expense' || (a.type === 'asset' && !/bank|cash|undeposited|receivable/i.test(a.name));

/** Under a date field: where an entry dated in a closed month posts (Q48, F-37), or nothing. */
export function useClosedHint(): (date: string) => string | null {
  const q = useQuery<{ periods: Period[] }>('periods.list', {});
  return (date) => {
    const hint = q.data ? closedMonthHint(date, q.data.periods) : null;
    return hint ? `${hint}.` : null;
  };
}

/** A section's rows not here yet: faint bars while they load, a red sentence and Try again when they didn't. */
export function Waiting({ error, what, retry }: { error: string | null; what: string; retry?: () => void }) {
  if (error)
    return (
      <p className="bp-ap-bad" role="alert">
        {`Couldn’t load ${what}.`} {retry ? <Link onPress={retry}>Try again</Link> : null}
      </p>
    );
  return (
    <SkeletonGroup label={`Loading ${what}`}>
      <Skeleton width="mid" />
      <Skeleton width="short" />
    </SkeletonGroup>
  );
}

/** A muted sentence: what a section says when it holds nothing, or a form's aside. */
export const Say = ({ children }: { children: ReactNode }) => <p className="bp-ap-say">{children}</p>;

/** A form's own red line: a refusal, or what's still missing. */
const Bad = ({ children }: { children: ReactNode }) => (
  <p className="bp-ap-bad" role="alert">
    {children}
  </p>
);

/** A form's buttons, in a row. */
export const Acts = ({ children }: { children: ReactNode }) => <div className="bp-ap-acts">{children}</div>;

// ── vendors ──

interface VendorDraft {
  name: string;
  email: string;
  termsDays: number;
  defaultAccountId: string;
}

function VendorForm({
  vendor,
  accounts,
  busy,
  onSave,
  onCancel,
}: {
  vendor: Vendor | null;
  accounts: Account[];
  busy: boolean;
  onSave: (v: VendorDraft) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(vendor?.name ?? '');
  const [email, setEmail] = useState(vendor?.email ?? '');
  const [terms, setTerms] = useState(String(vendor?.termsDays ?? 30));
  const [account, setAccount] = useState(vendor?.defaultAccountId ?? '');
  const termsOk = /^\d{1,3}$/.test(terms.trim()) && Number(terms) <= 365;
  const ok = !!name.trim() && termsOk;
  return (
    <form
      className="bp-ap-form"
      aria-label={vendor ? `Change ${vendor.name}` : 'A new vendor'}
      onSubmit={(e) => {
        e.preventDefault();
        if (ok && !busy)
          onSave({
            name: name.trim(),
            email: email.trim(),
            termsDays: Number(terms),
            defaultAccountId: account,
          });
      }}
    >
      <div className="bp-ap-wide">
        <TextField label="Name" value={name} onChange={setName} autoFocus />
      </div>
      <TextField
        label="Email for purchase orders"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="Optional"
      />
      <TextField label="Pay within (days)" inputMode="numeric" value={terms} onChange={setTerms} />
      <div className="bp-ap-wide">
        <SelectField
          label="Usual account"
          value={account}
          onChange={setAccount}
          options={[
            { value: '', label: 'None' },
            ...accounts.map((a) => ({ value: a.id, label: accountLabel(a) })),
          ]}
        />
      </div>
      <Acts>
        <Button type="submit" variant="primary" isDisabled={!ok || busy}>
          {vendor ? 'Save the vendor' : 'Add the vendor'}
        </Button>
        <Link onPress={onCancel} isDisabled={busy}>
          Cancel
        </Link>
      </Acts>
    </form>
  );
}

export function VendorsSection({ toast, onChanged }: { toast: Toast; onChanged: () => void }) {
  const vendors = useQuery<{ vendors: Vendor[] }>('vendors.list', {});
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', { includeInactive: false });
  const [open, setOpen] = useState<string | 'new' | null>(null);
  const act = useAct(toast, async () => {
    setOpen(null);
    await vendors.reload();
    onChanged();
  });
  const expense = (accounts.data?.accounts ?? []).filter(billable);
  const rows: Row[] = (vendors.data?.vendors ?? []).map((v) =>
    open === v.id
      ? {
          id: v.id,
          name: v.name,
          lines: [
            {
              part: (
                <VendorForm
                  vendor={v}
                  accounts={expense}
                  busy={act.busy}
                  onCancel={() => setOpen(null)}
                  onSave={(n) =>
                    void act.run(
                      'vendors.update',
                      {
                        id: v.id,
                        name: n.name,
                        email: n.email || null,
                        termsDays: n.termsDays,
                        defaultAccountId: n.defaultAccountId || null,
                      },
                      `${n.name} is saved.`,
                    )
                  }
                />
              ),
            },
          ],
        }
      : {
          id: v.id,
          name: v.name,
          label: v.name,
          lines: [
            {
              text: `Pay within ${v.termsDays} days${v.email ? ` · ${v.email}` : ''}${!v.active ? ' · switched off' : ''}`,
            },
          ],
          actions: [
            { id: 'change', text: 'Change', onPress: () => setOpen(v.id) },
            {
              id: 'onoff',
              text: v.active ? 'Switch off' : 'Switch on',
              isDisabled: act.busy,
              onPress: () =>
                void act.run(
                  'vendors.update',
                  { id: v.id, active: !v.active },
                  v.active ? `${v.name} is switched off.` : `${v.name} is back on.`,
                ),
            },
          ],
        },
  );
  return (
    <Section title="Vendors">
      {open !== 'new' && (
        <div className="bp-ap-tools">
          <Link onPress={() => setOpen('new')}>Add a vendor</Link>
        </div>
      )}
      {open === 'new' && (
        <VendorForm
          vendor={null}
          accounts={expense}
          busy={act.busy}
          onCancel={() => setOpen(null)}
          onSave={(v) =>
            void act.run(
              'vendors.create',
              {
                name: v.name,
                ...(v.email ? { email: v.email } : {}),
                termsDays: v.termsDays,
                ...(v.defaultAccountId ? { defaultAccountId: v.defaultAccountId } : {}),
              },
              `${v.name} is a vendor.`,
            )
          }
        />
      )}
      {!vendors.data ? (
        <Waiting error={vendors.error} what="the vendors" retry={() => void vendors.reload()} />
      ) : (
        <RowList
          label="Vendors"
          rows={rows}
          empty="No vendors yet. Add the suppliers and payees whose bills come in."
        />
      )}
    </Section>
  );
}

// ── a bill or a credit note, straight to accounts ──

interface Line {
  accountId: string;
  description: string;
  amount: string;
}
const NO_LINE: Line = { accountId: '', description: '', amount: '' };

/** The bill (or the credit note) being entered, opened from the page's head; `onClose` when saved or not. */
export function BillEntry({
  toast,
  timeZone,
  credit,
  onClose,
  onChanged,
}: {
  toast: Toast;
  timeZone?: string;
  /** Opened as a credit note (Enter a vendor credit). */
  credit?: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const vendors = useQuery<{ vendors: Vendor[] }>('vendors.list', {});
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', { includeInactive: false });
  const zones = useQuery<{ zones: { components: { code: string }[] }[] }>('tax.listZones', {});
  const hint = useClosedHint();
  const [kind, setKind] = useState<'bill' | 'credit'>(credit ? 'credit' : 'bill');
  const [vendorId, setVendorId] = useState('');
  const [ref, setRef] = useState('');
  const [billDate, setBillDate] = useState(todayIn(timeZone));
  const [dueDate, setDueDate] = useState('');
  const [creditFor, setCreditFor] = useState('');
  const [lines, setLines] = useState<Line[]>([NO_LINE]);
  const [tax, setTax] = useState('');
  const bills = useQuery<{ bills: VendorBill[] }>(
    'vendorBills.list',
    kind === 'credit' && vendorId ? { vendorId } : null,
  );
  const act = useAct(toast, async () => {
    onClose();
    onChanged();
  });
  const save = useSaveKey(JSON.stringify([kind, vendorId, ref, billDate, dueDate, creditFor, lines, tax]));
  const live = (vendors.data?.vendors ?? []).filter((v) => v.active);
  const vendor = live.find((v) => v.id === vendorId);
  const accountList = (accounts.data?.accounts ?? []).filter(billable);
  // Recoverable tax on a bill (input tax credits) is GST or HST; BC PST is part of the line amounts.
  const taxCode =
    (zones.data?.zones ?? [])
      .flatMap((z) => z.components.map((c) => c.code))
      .find((c) => c === 'HST' || c === 'GST') ?? 'GST';
  const cents = lines.map((l) => parseMoney(l.amount));
  const taxCents = tax.trim() ? parseMoney(tax) : 0;
  const linesOk = lines.every((l, i) => l.accountId && l.description.trim() && (cents[i] ?? 0) > 0);
  const ok = !!vendor && !!ref.trim() && !!billDate && linesOk && taxCents !== null && taxCents >= 0;
  const total = cents.reduce<number>((t, c) => t + (c ?? 0), 0) + (taxCents ?? 0);
  const setLine = (i: number, patch: Partial<Line>) =>
    setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const forBills = (bills.data?.bills ?? []).filter(
    (b) => b.kind === 'bill' && b.heldCents + b.owingCents > 0,
  );
  const title = kind === 'bill' ? 'A new bill' : 'A vendor’s credit note';
  const accountOptions = [
    { value: '', label: 'Pick an account…' },
    ...accountList.map((a) => ({ value: a.id, label: accountLabel(a) })),
  ];
  return (
    <Section title={title}>
      {!vendors.data || !accounts.data ? (
        <Waiting error={vendors.error ?? accounts.error} what="the vendors and accounts" />
      ) : live.length === 0 ? (
        <Say>Add the vendor first (Vendors, below), then enter their bill.</Say>
      ) : (
        <form
          className="bp-ap-form bp-ap-bill"
          aria-label={title}
          onSubmit={(e) => {
            e.preventDefault();
            if (!ok || act.busy) return;
            const input = {
              vendorId,
              vendorRef: ref.trim(),
              billDate,
              ...(dueDate ? { dueDate } : {}),
              lines: lines.map((l, i) => ({
                accountId: l.accountId,
                description: l.description.trim(),
                amountCents: cents[i]!,
              })),
              taxes: taxCents ? [{ code: taxCode, taxCents }] : [],
            };
            void act.run<{ number: string }>(
              kind === 'bill' ? 'vendorBills.create' : 'vendorBills.createCredit',
              kind === 'credit' && creditFor ? { ...input, creditForId: creditFor } : input,
              kind === 'bill'
                ? 'The bill is waiting in To approve.'
                : 'The credit note is waiting in To approve.',
              { idempotencyKey: save.key },
            );
          }}
        >
          <div className="bp-ap-full">
            <Segmented
              label="What came in"
              items={[
                { id: 'bill', label: 'A bill' },
                { id: 'credit', label: 'A credit note' },
              ]}
              selectedKey={kind}
              onSelectionChange={(k) => setKind(k as 'bill' | 'credit')}
            />
          </div>
          <div className="bp-ap-wide">
            <SelectField
              label="Vendor"
              value={vendorId}
              onChange={(id) => {
                setVendorId(id);
                const v = live.find((x) => x.id === id);
                if (v?.defaultAccountId && lines.length === 1 && !lines[0]!.accountId)
                  setLine(0, { accountId: v.defaultAccountId });
              }}
              options={[
                { value: '', label: 'Pick a vendor…' },
                ...live.map((v) => ({ value: v.id, label: v.name })),
              ]}
            />
          </div>
          <TextField
            label={kind === 'bill' ? 'Their bill number' : 'Their credit note number'}
            value={ref}
            onChange={setRef}
          />
          <TextField
            label={kind === 'bill' ? 'Billed on' : 'Dated'}
            type="date"
            value={billDate}
            onChange={setBillDate}
            {...(hint(billDate) ? { description: hint(billDate) } : {})}
          />
          {kind === 'bill' && (
            <TextField
              label="Due"
              type="date"
              value={dueDate}
              onChange={setDueDate}
              description={`Leave it empty for the vendor’s terms${vendor ? ` (${vendor.termsDays} days)` : ''}.`}
            />
          )}
          {kind === 'credit' && (
            <div className="bp-ap-wide">
              <Say>
                For parts sent back on a return, enter their credit from Returns awaiting credit instead: it
                clears the return.
              </Say>
            </div>
          )}
          {kind === 'credit' && vendorId && (
            <div className="bp-ap-wide">
              <SelectField
                label="Against a bill"
                value={creditFor}
                onChange={setCreditFor}
                options={[
                  { value: '', label: 'None: a credit to use later' },
                  ...forBills.map((b) => ({
                    value: b.id,
                    label: `${b.number} · ${b.vendorRef} · ${money(b.totalCents)}`,
                  })),
                ]}
              />
            </div>
          )}
          {lines.map((l, i) => (
            <div key={i} className="bp-ap-line">
              <SelectField
                label="Account"
                value={l.accountId}
                onChange={(accountId) => setLine(i, { accountId })}
                options={accountOptions}
              />
              <TextField
                label="What it’s for"
                value={l.description}
                onChange={(description) => setLine(i, { description })}
              />
              <TextField
                label="Amount"
                inputMode="decimal"
                width="number"
                value={l.amount}
                onChange={(amount) => setLine(i, { amount })}
              />
              {lines.length > 1 && (
                <Link
                  label={`Remove line ${i + 1}`}
                  onPress={() => setLines(lines.filter((_, j) => j !== i))}
                >
                  Remove
                </Link>
              )}
            </div>
          ))}
          <TextField
            label={`${taxCode} on it (recoverable)`}
            inputMode="decimal"
            value={tax}
            onChange={setTax}
            placeholder="0.00"
          />
          <div className="bp-ap-wide">
            <Say>Total {money(total)}. Nothing posts until it’s approved in To approve.</Say>
          </div>
          <Acts>
            <Button
              onPress={() => setLines([...lines, { ...NO_LINE, accountId: vendor?.defaultAccountId ?? '' }])}
            >
              Another line
            </Button>
            <Button type="submit" variant="primary" isDisabled={!ok || act.busy} isBusy={act.busy}>
              {kind === 'bill' ? 'Save the bill' : 'Save the credit note'}
            </Button>
            <Link onPress={onClose} isDisabled={act.busy}>
              Cancel
            </Link>
          </Acts>
        </form>
      )}
    </Section>
  );
}

// ── an approved credit, used on one of that vendor's bills ──

function UseCredit({
  credit,
  bills,
  act,
  onCancel,
}: {
  credit: VendorBill;
  bills: VendorBill[];
  act: Act;
  onCancel: () => void;
}) {
  const [billId, setBillId] = useState('');
  const [amount, setAmount] = useState('');
  const left = credit.approvedCents - credit.settledCents;
  if (bills.length === 0) return <Say>Nothing from {credit.vendorName} is owing to use it on.</Say>;
  const target = bills.find((b) => b.id === billId);
  const cents = parseMoney(amount);
  const max = Math.min(left, target?.owingCents ?? left);
  const ok = !!target && cents !== null && cents > 0 && cents <= max;
  return (
    <div className="bp-ap-form">
      <div className="bp-ap-wide">
        <SelectField
          label="Use it on"
          value={billId}
          onChange={(id) => {
            setBillId(id);
            const b = bills.find((x) => x.id === id);
            if (b) setAmount((Math.min(left, b.owingCents) / 100).toFixed(2));
          }}
          options={[
            { value: '', label: 'Pick a bill…' },
            ...bills.map((b) => ({
              value: b.id,
              label: `${b.number} · ${b.vendorRef} · ${money(b.owingCents)} owing`,
            })),
          ]}
        />
      </div>
      {target && (
        <TextField
          label={`Amount (up to ${money(max)})`}
          inputMode="decimal"
          width="number"
          value={amount}
          onChange={setAmount}
        />
      )}
      <Acts>
        <Button
          variant="primary"
          isDisabled={!ok || act.busy}
          onPress={() =>
            void act.run(
              'vendorBills.applyCredit',
              { creditId: credit.id, billId, amountCents: cents },
              `${money(cents ?? 0)} of ${credit.number} went on ${target?.number}.`,
            )
          }
        >
          Use {money(ok ? cents! : 0)}
        </Button>
        <Link onPress={onCancel} isDisabled={act.busy}>
          Cancel
        </Link>
      </Acts>
    </div>
  );
}

export function VendorCreditsSection({
  toast,
  version,
  onChanged,
}: {
  toast: Toast;
  version: number;
  onChanged: () => void;
}) {
  const approved = useQuery<{ bills: VendorBill[] }>('vendorBills.list', { status: 'approved' }, [version]);
  const [open, setOpen] = useState<string | null>(null);
  const act = useAct(toast, async () => {
    setOpen(null);
    await approved.reload();
    onChanged();
  });
  const all = approved.data?.bills ?? [];
  const credits = all.filter((b) => b.kind === 'credit' && b.approvedCents - b.settledCents > 0);
  if (!approved.data || credits.length === 0) return null;
  const rows: Row[] = credits.map((c) => ({
    id: c.id,
    name: c.number,
    label: `${c.number} · ${c.vendorName}`,
    after: { text: ` ${c.vendorName}` },
    lines: [
      { text: `${c.vendorRef} · dated ${dayText(c.billDate)}` },
      ...(open === c.id
        ? [
            {
              part: (
                <UseCredit
                  credit={c}
                  bills={all.filter(
                    (b) => b.kind === 'bill' && b.vendorId === c.vendorId && b.owingCents > 0,
                  )}
                  act={act}
                  onCancel={() => setOpen(null)}
                />
              ),
            },
          ]
        : []),
    ],
    end: { figure: money(c.approvedCents - c.settledCents) },
    ...(open === c.id
      ? {}
      : { actions: [{ id: 'use', text: 'Use on a bill…', onPress: () => setOpen(c.id) }] }),
  }));
  return (
    <Section title="Vendor credits to use" count={credits.length}>
      <RowList label="Vendor credits to use" rows={rows} />
    </Section>
  );
}

// ── receipts for parts bought on jobs (BL-012) ──

function ApproveReceipt({ p, act, onDone }: { p: JobPurchase; act: Act; onDone: () => void }) {
  const vendors = useQuery<{ vendors: { id: string; name: string; active: boolean }[] }>('vendors.list', {});
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', { includeInactive: false });
  const cards = cardsOf(banks.data?.bankAccounts ?? [], accounts.data?.accounts ?? []);
  const [f, setF] = useState<ApproveForm | null>(null);
  if (!vendors.data || !banks.data || !accounts.data)
    return <Waiting error={vendors.error ?? banks.error ?? accounts.error} what="the approval" />;
  const form = f ?? approveDefaults(p, cards);
  const set = (patch: Partial<ApproveForm>) => setF({ ...form, ...patch });
  const blocker = approveBlocker(p, form);
  const gstText = form.gstCents === null ? '' : (form.gstCents / 100).toFixed(2);
  return (
    <div className="bp-ap-form">
      <SelectField
        label="Paid"
        value={form.paidHow}
        onChange={(v) => set({ paidHow: v as ApproveForm['paidHow'] })}
        options={[
          { value: 'on_account', label: 'On the supplier’s account' },
          { value: 'company_card', label: 'With a company card', isDisabled: !cards.length },
        ]}
      />
      {form.paidHow === 'company_card' ? (
        <SelectField
          label="Card"
          value={form.bankAccountId}
          onChange={(bankAccountId) => set({ bankAccountId })}
          options={cards.map((c) => ({ value: c.id, label: `${c.name}${c.last4 ? ` ·${c.last4}` : ''}` }))}
        />
      ) : null}
      <SelectField
        label="Vendor"
        value={form.vendorId}
        onChange={(vendorId) => set({ vendorId })}
        options={[
          { value: '', label: form.paidHow === 'on_account' ? 'Pick the vendor' : 'None' },
          ...vendors.data.vendors.filter((v) => v.active).map((v) => ({ value: v.id, label: v.name })),
        ]}
      />
      {!p.jobId ? (
        <SelectField
          label="Account"
          value={form.accountId}
          onChange={(accountId) => set({ accountId })}
          options={[
            { value: '', label: 'Pick the account' },
            ...overheadAccounts(accounts.data.accounts).map((a) => ({ value: a.id, label: accountLabel(a) })),
          ]}
        />
      ) : null}
      <TextField
        label="GST on it"
        inputMode="decimal"
        value={gstText}
        placeholder="0.00"
        onChange={(v) => set({ gstCents: v.trim() ? (parseMoney(v) ?? 0) : 0 })}
        description="Claimed back, not cost. PST stays in the cost."
      />
      <TextField
        label="Receipt or invoice number"
        value={form.receiptRef}
        onChange={(receiptRef) => set({ receiptRef })}
      />
      {blocker ? (
        <div className="bp-ap-wide" role="note">
          <Say>{blocker}</Say>
        </div>
      ) : null}
      <Acts>
        <Button
          variant="primary"
          isDisabled={act.busy || !!blocker}
          onPress={() =>
            void (async () => {
              const ok = await act.run('jobPurchases.approve', approveInput(p, form), 'Approved and posted.');
              if (ok) onDone();
            })()
          }
        >
          Approve
        </Button>
        <Link onPress={onDone}>Cancel</Link>
      </Acts>
    </div>
  );
}

function VoidReceipt({ p, act, onDone }: { p: JobPurchase; act: Act; onDone: () => void }) {
  const [reason, setReason] = useState('');
  return (
    <div className="bp-ap-form">
      <TextField
        label="Why"
        value={reason}
        onChange={setReason}
        placeholder="Recorded twice, the wrong job…"
      />
      <Acts>
        <Button
          isDisabled={act.busy || !reason.trim()}
          onPress={() =>
            void (async () => {
              const ok = await act.run(
                'jobPurchases.void',
                { purchaseId: p.id, reason: reason.trim() },
                'Voided.',
              );
              if (ok) onDone();
            })()
          }
        >
          Void it
        </Button>
        <Link onPress={onDone}>Keep it</Link>
      </Acts>
    </div>
  );
}

const receiptTone = (p: JobPurchase): RowTone =>
  p.status === 'approved' ? 'muted' : p.status === 'void' ? 'bad' : 'wait';

/** One receipt as a row: what, where, who, how much, its photo, and what can be done. */
function receiptRow(
  p: JobPurchase,
  open: 'approve' | 'void' | null,
  setOpen: (o: 'approve' | 'void' | null) => void,
  act: Act,
): Row {
  const total = totalOf(p);
  const name = `${p.number} · ${supplierText(p)}${p.kind === 'credit' ? ' (credit)' : ''}`;
  const extra = `${p.note ? ` · ${p.note}` : ''}${p.voidReason ? ` · ${p.voidReason}` : ''}`;
  return {
    id: p.id,
    name,
    label: name,
    lines: [
      {
        text: `${whereText(p)}${p.staffName ? ` · ${p.staffName}` : ''} · ${dayText(p.purchasedOn)}${p.receiptRef ? ` · ${p.receiptRef}` : ''}`,
      },
      { word: statusText(p), tone: receiptTone(p) },
      {
        part: (
          <p className="bp-ap-say">
            {p.receipt?.url ? (
              <Link href={p.receipt.url} isExternal>
                {receiptText(p)}
              </Link>
            ) : (
              receiptText(p)
            )}
            {extra}
          </p>
        ),
      },
      ...(open === 'approve'
        ? [{ part: <ApproveReceipt p={p} act={act} onDone={() => setOpen(null)} /> }]
        : []),
      ...(open === 'void' ? [{ part: <VoidReceipt p={p} act={act} onDone={() => setOpen(null)} /> }] : []),
    ],
    ...(total !== null ? { end: { figure: money(p.kind === 'credit' ? -total : total) } } : {}),
    ...(p.status === 'submitted' && !open
      ? {
          atRest: 2 as const,
          actions: [
            { id: 'approve', text: 'Approve…', isDisabled: act.busy, onPress: () => setOpen('approve') },
            { id: 'void', text: 'Void…', isDisabled: act.busy, onPress: () => setOpen('void') },
          ],
        }
      : {}),
  };
}

/** Beside the bills: every receipt waiting for the office. */
export function ReceiptsSection({ toast, onChanged }: { toast: Toast; onChanged: () => void }) {
  const q = useQuery<{ purchases: JobPurchase[] }>('jobPurchases.list', { status: 'submitted' });
  const [open, setOpen] = useState<{ id: string; doing: 'approve' | 'void' } | null>(null);
  const act = useAct(toast, async () => {
    await q.reload();
    onChanged();
  });
  return (
    <Section title="Receipts to approve" {...(q.data ? { count: q.data.purchases.length } : {})}>
      {!q.data ? (
        <Waiting error={q.error} what="the receipts" retry={() => void q.reload()} />
      ) : (
        <RowList
          label="Receipts to approve"
          rows={q.data.purchases.map((p) =>
            receiptRow(
              p,
              open?.id === p.id ? open.doing : null,
              (o) => setOpen(o ? { id: p.id, doing: o } : null),
              act,
            ),
          )}
          empty="No receipt is waiting for approval."
        />
      )}
    </Section>
  );
}

// ── returns awaiting a vendor's credit (BL-013) ──

interface Awaiting {
  rows: AwaitingRow[];
  openCents: number;
  ledgerCents: number;
  differenceCents: number;
}

export function ReturnsSection({
  toast,
  timeZone,
  version,
  onChanged,
}: {
  toast: Toast;
  timeZone?: string;
  /** Bumped when a credit is approved elsewhere on the page, so what's open is read again. */
  version: number;
  onChanged: () => void;
}) {
  const q = useQuery<Awaiting>('vendorReturns.awaitingCredit', {}, [version]);
  const [open, setOpen] = useState<{ id: string; doing: 'credit' | 'close' } | null>(null);
  if (!q.data) return null;
  if (q.data.rows.length === 0 && q.data.differenceCents === 0) return null;
  const done = async (said: string) => {
    toast(said);
    setOpen(null);
    await q.reload();
    onChanged();
  };
  const rows: Row[] = q.data.rows.map((r) => ({
    id: r.returnId,
    name: r.number,
    label: `${r.number} ${r.vendorName}`,
    ...(r.daysOut > 30 ? { word: { text: daysOutText(r.daysOut), tone: 'wait' as const } } : {}),
    after: { text: ` ${r.vendorName}` },
    lines: [
      {
        text: `Sent back ${dayText(r.sentOn)}${r.daysOut > 0 && r.daysOut <= 30 ? ` · ${daysOutText(r.daysOut)}` : ''} · ${r.rma ? `RMA ${r.rma}` : 'no RMA yet'}`,
      },
      ...(open?.id === r.returnId
        ? [
            {
              part: (
                <ReturnWork
                  id={r.returnId}
                  doing={open.doing}
                  {...(timeZone ? { timeZone } : {})}
                  onClose={() => setOpen(null)}
                  onDone={(said) => void done(said)}
                />
              ),
            },
          ]
        : []),
    ],
    end: { figure: money(r.openCents) },
    atRest: 2,
    actions: [
      {
        id: 'credit',
        text: 'Enter their credit',
        label: `Enter ${r.vendorName}’s credit for ${r.number}`,
        onPress: () => setOpen({ id: r.returnId, doing: 'credit' }),
      },
      {
        id: 'close',
        text: 'Close…',
        label: `Close ${r.number} without a full credit`,
        onPress: () => setOpen({ id: r.returnId, doing: 'close' }),
      },
    ],
  }));
  return (
    <Section title="Returns awaiting credit">
      <div className="bp-ap-tools">
        <span className="bp-ap-figure">{money(q.data.openCents)}</span>
      </div>
      {q.data.differenceCents !== 0 && (
        <Notice
          title={`The returns awaiting credit account shows ${money(q.data.ledgerCents)}; the returns still open come to ${money(q.data.openCents)}. Something posted there that isn’t a return, or a posting is still on its way.`}
        />
      )}
      <RowList
        label="Returns awaiting credit"
        rows={rows}
        empty="No parts are waiting for a vendor’s credit."
      />
    </Section>
  );
}

/** The return read fresh, then its credit form or its close dialog. */
function ReturnWork({
  id,
  doing,
  timeZone,
  onClose,
  onDone,
}: {
  id: string;
  doing: 'credit' | 'close';
  timeZone?: string;
  onClose: () => void;
  onDone: (said: string) => void;
}) {
  const q = useQuery<VendorReturn>('vendorReturns.get', { id });
  if (!q.data) return <Waiting error={q.error} what="the return" retry={() => void q.reload()} />;
  return doing === 'credit' ? (
    <CreditForReturn r={q.data} {...(timeZone ? { timeZone } : {})} onClose={onClose} onDone={onDone} />
  ) : (
    <CloseReturn r={q.data} onClose={onClose} onDone={onDone} />
  );
}

function CreditForReturn({
  r,
  timeZone,
  onClose,
  onDone,
}: {
  r: VendorReturn;
  timeZone?: string;
  onClose: () => void;
  onDone: (said: string) => void;
}) {
  const [ref, setRef] = useState('');
  const [dated, setDated] = useState(todayIn(timeZone));
  const [lines, setLines] = useState<CreditLineForm[]>(() => creditForm(r));
  const [tax, setTax] = useState('');
  const hint = useClosedHint();
  // recoverable tax on a credit is GST or HST, as on a bill; BC PST is in the prices
  const zones = useQuery<{ zones: { components: { code: string }[] }[] }>('tax.listZones', {});
  const taxCode =
    (zones.data?.zones ?? [])
      .flatMap((z) => z.components.map((c) => c.code))
      .find((c) => c === 'HST' || c === 'GST') ?? 'GST';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveKey(JSON.stringify([r.id, ref, dated, lines, tax]));
  const built = creditLines(lines);
  const taxCents = tax.trim() ? parseMoney(tax) : 0;
  const subtotal = 'lines' in built ? creditSubtotal(built.lines) : 0;
  const problem =
    'problem' in built
      ? built.problem
      : !ref.trim()
        ? 'Their credit note number.'
        : taxCents === null || taxCents < 0
          ? `The ${taxCode} like 1.25, or leave it empty.`
          : null;
  const set = (i: number, patch: Partial<CreditLineForm>) =>
    setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const enter = async () => {
    if (problem || !('lines' in built) || busy) return;
    setBusy(true);
    setError(null);
    try {
      const c = await client.call<{ number: string }>(
        'vendorBills.createCredit',
        {
          vendorId: r.vendorId,
          vendorRef: ref.trim(),
          billDate: dated,
          lines: built.lines,
          taxes: taxCents ? [{ code: taxCode, taxCents }] : [],
        },
        { idempotencyKey: save.key },
      );
      onDone(`${c.number}, ${r.vendorName}’s credit for ${r.number}, is waiting in To approve.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="bp-ap-form"
      aria-label={`${r.vendorName}’s credit for ${r.number}`}
      onSubmit={(e) => {
        e.preventDefault();
        void enter();
      }}
    >
      <TextField label="Their credit note number" maxLength={100} value={ref} onChange={setRef} />
      <TextField
        label="Dated"
        type="date"
        value={dated}
        onChange={setDated}
        {...(hint(dated) ? { description: hint(dated) } : {})}
      />
      <ul className="bp-ap-full bp-ap-credit-lines" aria-label="What they credit">
        {lines.map((l, i) => (
          <li key={l.returnLineId} className="bp-ap-credit-line">
            <p className="bp-ap-credit-name">
              {l.name}
              <span className="bp-ap-say">{l.left} left to credit</span>
            </p>
            <TextField
              label="How many"
              inputMode="decimal"
              width="number"
              value={l.quantity}
              onChange={(quantity) => set(i, { quantity })}
            />
            <TextField
              label="Each"
              inputMode="decimal"
              width="number"
              value={l.price}
              onChange={(price) => set(i, { price })}
            />
          </li>
        ))}
      </ul>
      <TextField
        label={`${taxCode} on it (optional)`}
        inputMode="decimal"
        placeholder="0.00"
        value={tax}
        onChange={setTax}
      />
      <div className="bp-ap-wide">
        <Say>
          {money(subtotal + (taxCents ?? 0))} in all. It waits in To approve; approved, it clears the return
          and can go on their bills.
        </Say>
      </div>
      {problem && (
        <div className="bp-ap-full">
          <Bad>{problem}</Bad>
        </div>
      )}
      {error && (
        <div className="bp-ap-full">
          <Bad>{error}</Bad>
        </div>
      )}
      <Acts>
        <Button type="submit" variant="primary" isDisabled={!!problem || busy} isBusy={busy}>
          Save the credit note
        </Button>
        <Link onPress={onClose} isDisabled={busy}>
          Cancel
        </Link>
      </Acts>
    </form>
  );
}

function CloseReturn({
  r,
  onClose,
  onDone,
}: {
  r: VendorReturn;
  onClose: () => void;
  onDone: (said: string) => void;
}) {
  const choices: CloseChoice[] = mayNet(r) ? ['credited_less', 'netted', 'kept'] : ['credited_less', 'kept'];
  const [how, setHow] = useState<CloseChoice>('credited_less');
  const [note, setNote] = useState('');
  return (
    <ConfirmDialog
      isOpen
      onClose={onClose}
      title={`Close ${r.number} without a full credit?`}
      confirmLabel="Close the return"
      cancelLabel="Cancel"
      onConfirm={async () => {
        try {
          await client.call('vendorReturns.close', {
            returnId: r.id,
            how,
            ...(note.trim() ? { note: note.trim() } : {}),
          });
        } catch (e) {
          throw new Error(errorText(e), { cause: e });
        }
        onDone(`${r.number} is closed: ${CLOSE_LABEL[how].toLowerCase()}.`);
      }}
    >
      <Say>{closeOutcome(r, how)}</Say>
      <ChoiceGroup
        selectionMode="single"
        label={`What ${r.vendorName} did`}
        value={how}
        onChange={(v) => setHow(v as CloseChoice)}
      >
        {choices.map((c) => (
          <Choice key={c} value={c}>
            {CLOSE_LABEL[c]}
          </Choice>
        ))}
      </ChoiceGroup>
      <TextField
        label="Note (optional)"
        maxLength={500}
        value={note}
        placeholder="Restocking fee; past their return window"
        onChange={setNote}
      />
    </ConfirmDialog>
  );
}
